import React, { useEffect, useState } from 'react';
import { DeleteJson, GetJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, Permissions, REST_API } from '@awp/constants';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { FormCheckbox, FormInput, FormSelect, FormTextArea } from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import { useVMSContext } from './VmsContext';
import { getCrud } from '@awp/shared/Crud';
import isEqual from 'lodash/isEqual';
import VmsService from '@components/VariableMessageSigns/VmsService';

const VmsEdit = () => {
  const {
    mode,
    setMode,
    vms,
    data,
    setData,
    loading,
    setLoading,
    weatherStationOptions,
    setSelectedWeatherStationId,
    sensorPlacements,
    placementLoading,
  } = useVMSContext();

  const vmsId = vms?.Id;

  const [vmsData, setVmsData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [initialDto, setInitialDto] = useState({});

  const crudService = new VmsService(mode);
  const { title, readOnly, successMessage, entityName, buttonLabel } = crudService.crud;

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setVmsData({ Name: '', Description: '', IsActive: 'Y' });
      setInitialDto(crudService.mapInitialValues({ entity: vmsData, mode }));
      return;
    }

    setLoading(true);
    const url = REST_API.VMS + vmsId;
    GetJson(url)
      .then((response) => response.json())
      .then((_vms) => {
        setVmsData(_vms);
        setInitialDto(crudService.mapInitialValues({ entity: _vms, mode }));
        setSelectedWeatherStationId(_vms.WeatherStationId);
        setLoading(false);
      });
  }, []);

  const updateVms = (setSubmitting, resetForm, setErrors, vmsDto) => {
    const url = REST_API.VMS + vmsDto.Id;
    console.log(vmsDto);
    PutJson(url, vmsDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const _vms = await response.json();

          setVmsData(_vms);
          updateData(_vms);

          const updatedDto = crudService.mapInitialValues({ entity: _vms, mode });
          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ VariableMessageSign: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createVms = (setSubmitting, resetForm, setErrors, vmsDto) => {
    const url = REST_API.VMS;

    PostJson(url, vmsDto)
      .then(async (response) => {
        if (response.ok) {
          const _vms = await response.json();
          setVmsData(_vms);
          updateData(_vms);
          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);

          const updatedDto = crudService.mapInitialValues({ entity: _vms, mode: FORM_MODE.VIEW });
          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ VariableMessageSign: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteVms = () => {
    const id = vmsData.Id;
    const url = REST_API.VMS + id;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          setVmsData({});
          removeData(id);
          toast.success(`${entityName} successfully deleted`);
          setMode(FORM_MODE.LIST);
        } else if (response.status === 412) {
          const errors = await response.json();
          //setErrors(errors);
          if (errors.entity) setEntityErrors({ VariableMessageSign: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };
  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    const vmsDto = {
      ...vmsData,
      Name: values.name,
      StateKey: values.stateKey,
      Description: values.description,
      WeatherStationId: values.weatherStation.value,
      Location: values.location.replace(/\s+$/, ''),
      IsActive: values.isActive ? 'Y' : 'N',
      SensorPlacementId: values.sensorPlacement.value,
    };
    if (mode === FORM_MODE.EDIT) {
      updateVms(setSubmitting, resetForm, setErrors, vmsDto);
    } else {
      createVms(setSubmitting, resetForm, setErrors, vmsDto);
    }
  };

  const updateData = (v) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === v.Id);
      if (index < 0) return;
      data[index] = v;
      setData([...data]);
    } else {
      setData([...data, v]);
    }
  };

  const removeData = (Id) => {
    if (mode === FORM_MODE.EDIT) {
      let _data = [...data];
      let _removedData = [];
      _data.map((o) => {
        if (o.Id !== Id) {
          _removedData.push(o);
        }
      });
      setData([..._removedData]);
    }
  };

  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleWeatherStationChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedWeatherStationId(selectedOption.value);
    }
  };

  return (
    <>
      {loading ? (
        <Container>
          <AwpSpinner />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={{
              name: vmsData.Name ?? '',
              stateKey: vmsData.StateKey ?? '',
              weatherStation:
                vmsData && vmsData.WeatherStationId > 0
                  ? {
                      value: vmsData.WeatherStationId,
                      label: vmsData.WeatherStationNumber + ' - ' + vmsData.WeatherStationName,
                    }
                  : null,
              sensorPlacement:
                vmsData && vmsData.SensorPlacementId > 0
                  ? {
                      value: vmsData.SensorPlacementId,
                      label: vmsData.SensorPlacementName,
                    }
                  : null,
              location: vmsData.Location,
              isActive: mode === FORM_MODE.CREATE || vmsData.IsActive === 'Y',
              description: vmsData.Description ?? '',
            }}
            validateOnMount={true}
            validationSchema={Yup.object({
              name: Yup.string().max(50, 'Maximum 50 characters are allowed').required('Name is a required field'),
              stateKey: Yup.string()
                .max(50, 'Maximum 50 characters are allowed')
                .required('State Key is a required field'),
              location: Yup.string()
                .max(50, 'Maximum 50 characters are allowed')
                .required('Location is a required field'),
              weatherStation: Yup.object().nullable().required('Weather Station is a required field'),
              sensorPlacement: Yup.object().nullable().required('Sensor Placment is a required field'),
              description: Yup.string()
                .max(250, 'Maximum 250 characters are allowed')
                .required('Description is a required field'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <>
                {/* {props.values && <pre>{JSON.stringify(props.values, null, 2)}</pre>}
                {vmsData && <pre>{JSON.stringify(vmsData, null, 2)}</pre>} */}
                <Form>
                  <span className="awp-text-btn" onClick={() => setMode('list')}>
                    List Variable Message Signs
                  </span>
                  <span className="mx-3">&gt;</span>
                  <span>{title}</span>
                  <Card border="light" className="page-card">
                    <Card.Header>
                      <Row className="mt-4"></Row>
                      {crudService.crud.getHeader()}
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput label="Name" readOnly={readOnly} name="name" id="name" />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput label="State Key" readOnly={readOnly} name="stateKey" id="stateKey" />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormSelect
                            readOnly={readOnly}
                            name="weatherStation"
                            id="weatherStation"
                            label="Weather Station"
                            options={weatherStationOptions}
                            isMulti={false}
                            postChange={(selectedOption) => {
                              handleWeatherStationChange(selectedOption);
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormSelect
                            readOnly={readOnly}
                            name="sensorPlacement"
                            id="sensorPlacement"
                            label="Sensor Placement"
                            options={sensorPlacements}
                            isMulti={false}
                            isLoading={placementLoading}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput label="Location" readOnly={readOnly} name="location" id="location" />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormCheckbox readOnly={readOnly} id="isActive" name="isActive" label="Active" />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={8} className="mb-4">
                          <FormTextArea
                            readOnly={readOnly}
                            instruction="(250 Characters max)"
                            label="Description"
                            id="description"
                            name="description"
                            rows={3}
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                    <Card.Footer>
                      {!showDeleteConfirm ? (
                        <Row className="my-2">
                          <AwpFormActionButtons
                            title=" "
                            entityName={entityName}
                            buttonLabel={buttonLabel}
                            mode={mode}
                            setMode={setMode}
                            onClickDelete={onClickDelete}
                            showDeleteBtn={true}
                            permissions={[Permissions.WriteVms]}
                            readOnly={readOnly}
                            isAlignLeft={true}
                            btnCancelName={'Close'}
                            props={props}
                            isPristine={isEqual(props.values, initialDto)}
                          />
                        </Row>
                      ) : (
                        <Row className="my-2">
                          <AwpYesNoDialog action={deleteVms} setShowYesNoConfirm={setShowDeleteConfirm} props={props} />
                        </Row>
                      )}
                    </Card.Footer>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default VmsEdit;
